/**
 * `aethereum distill` — v4-S11 D1: turn your own session history into skill
 * candidates, WITHOUT us ever calling a model or the network during analysis.
 *
 *   aethereum distill --scan            read-only report of the candidates
 *   aethereum distill                   scan, then write skills/<slug>/DRAFT.md
 *                                       (a ready-to-paste prompt YOUR agent runs
 *                                       to author the SKILL.md; we never invoke
 *                                       any model with your credentials)
 *   aethereum distill --publish <slug>  preview the EXACT outbound text of
 *                                       skills/<slug>/SKILL.md and consent-gate
 *                                       the two publish paths (team ruleset /
 *                                       marketplace). The CLI PRINTS; it never
 *                                       POSTs the skill anywhere itself.
 *
 * The analysis is purely local (see src/sessions/distill.ts). The only thing
 * that can ever leave the machine is a SKILL.md the user explicitly previews
 * and confirms, and even then this command only prints what to send and where.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
import { createInterface } from "node:readline";
import {
  type DistillCandidate,
  type DistillScan,
  persistDistillScan,
  scanForCandidates,
  backtestSavings,
} from "../sessions/distill.js";
import { readIndex } from "../sessions/index-store.js";
import { resolveProject } from "../hive/config-store.js";

export interface DistillCliOptions {
  home?: string;
  cwd?: string;
  /** Codex sessions dir override (tests / fixtures). */
  codexDir?: string;
  /** TTY override (tests). Defaults to process stdin+stdout being TTYs. */
  isTTY?: boolean;
  /** Prompt override (tests). Returns the user's raw answer line. */
  ask?: (question: string) => Promise<string>;
  out?: (line: string) => void;
  err?: (line: string) => void;
  now?: number;
}

function getFlag(name: string, args: string[]): string | undefined {
  const idx = args.indexOf(`--${name}`);
  if (idx === -1) return undefined;
  const value = args[idx + 1];
  if (value === undefined || value.startsWith("--")) return undefined;
  return value;
}

function stdinAsk(question: string): Promise<string> {
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) =>
    rl.question(question, (answer) => {
      rl.close();
      resolve(answer);
    }),
  );
}

// ── Report rendering ─────────────────────────────────────────────────────────

function pad(s: string, w: number): string {
  return s.length >= w ? s : s + " ".repeat(w - s.length);
}

/** The span the indexed sessions cover, in whole days (>= 1), from the index ts range. */
function scanWindowDays(home?: string): number {
  try {
    const lines = readIndex(home);
    if (lines.length === 0) return 1;
    let min = Infinity;
    let max = -Infinity;
    for (const l of lines) {
      if (l.ts < min) min = l.ts;
      if (l.ts > max) max = l.ts;
    }
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return 1;
    return Math.max(1, Math.round((max - min) / 86_400_000));
  } catch {
    return 1;
  }
}

function printReport(scan: DistillScan, out: (line: string) => void, home?: string): void {
  out(
    `Scanned ${scan.sessionsScanned} session${scan.sessionsScanned === 1 ? "" : "s"} across ${scan.transcriptsScanned} transcript${scan.transcriptsScanned === 1 ? "" : "s"} (local only, nothing left this machine).`,
  );
  if (scan.candidates.length === 0) {
    out("No skill candidates found. Patterns need to recur (usually across 2+ sessions) before they show up here.");
    return;
  }
  const days = scanWindowDays(home);
  const est = new Map(backtestSavings(scan, days).map((e) => [e.slug, e]));
  out("");
  out(`${pad("KIND", 20)}${pad("SCORE", 7)}${pad("~TOK/WK", 10)}${pad("SLUG", 40)}TITLE`);
  for (const c of scan.candidates) {
    const perWeek = est.get(c.slug)?.estTokensPerWeek ?? 0;
    out(`${pad(c.kind, 20)}${pad(String(c.score), 7)}${pad(`~${perWeek.toLocaleString()}`, 10)}${pad(c.slug.slice(0, 38), 40)}${c.title}`);
  }
  for (const c of scan.candidates) {
    const e = est.get(c.slug);
    out("");
    out(`· ${c.slug} (${c.kind}, score ${c.score})`);
    out(`  ${c.title}`);
    out(`  Suggestion: ${c.suggestion}`);
    if (e) out(`  Rough estimate: ~${e.estTokensPerWeek.toLocaleString()} tokens/week if a skill absorbed this (${e.occurrences} occurrences over ~${days} day${days === 1 ? "" : "s"}; an estimate, not a measurement).`);
    for (const ev of c.evidence) out(`    evidence: ${ev}`);
  }
  out("");
  out(`${scan.candidates.length} candidate${scan.candidates.length === 1 ? "" : "s"}. Run 'aethereum distill' (no flag) to draft skill prompts, or 'aethereum distill --weekly' for a PR-ready summary.`);
}

/** v6-S14: a Monday-ritual, PR-ready summary of the week's top skill candidates. */
function printWeekly(scan: DistillScan, out: (line: string) => void, home?: string): void {
  const days = scanWindowDays(home);
  const est = backtestSavings(scan, days).sort((a, b) => b.estTokensPerWeek - a.estTokensPerWeek);
  const bySlug = new Map(scan.candidates.map((c) => [c.slug, c]));
  const top = est.slice(0, 5);
  const totalPerWeek = est.reduce((s, e) => s + e.estTokensPerWeek, 0);
  out("## Skill candidates this week (Aethereum distill)");
  out("");
  out(`Scanned ${scan.sessionsScanned} local sessions over ~${days} day${days === 1 ? "" : "s"}. Estimated tokens/week these skills could save if adopted: ~${totalPerWeek.toLocaleString()} (a rough estimate from occurrence counts, not a measurement). Nothing left this machine.`);
  out("");
  if (top.length === 0) {
    out("No recurring patterns strong enough to extract yet.");
    return;
  }
  for (const e of top) {
    const c = bySlug.get(e.slug);
    if (!c) continue;
    out(`- **${c.slug}** (~${e.estTokensPerWeek.toLocaleString()} tok/wk, ${e.occurrences}x): ${c.title}. ${c.suggestion}`);
  }
  out("");
  out("Draft them with `aethereum distill`, then have your agent author each `skills/<slug>/SKILL.md` and open a PR.");
}

// ── DRAFT.md (the prompt the USER'S agent runs; we never invoke a model) ─────

export function buildDraftPrompt(candidate: DistillCandidate): string {
  const evidence = candidate.evidence.map((e) => `- ${e}`).join("\n");
  return [
    `Author a reusable agent skill at skills/${candidate.slug}/SKILL.md from the evidence below.`,
    "",
    "Format (Anthropic skill format): the file must start with YAML frontmatter, then markdown instructions:",
    "",
    "---",
    `name: ${candidate.slug}`,
    "description: <one line: what this skill does and when an agent should use it>",
    "---",
    "",
    "<concise markdown instructions for the agent>",
    "",
    `Observed pattern (${candidate.kind}): ${candidate.title}`,
    "Evidence (short samples from local session transcripts, truncated and path-stripped):",
    evidence || "- (see the distill report)",
    "",
    `What the skill should do: ${candidate.suggestion}`,
    "",
    "Requirements:",
    "- Keep the instructions concise and imperative; an agent will follow them verbatim.",
    "- Generalize from the evidence; do not copy machine-specific details.",
    "- Never include personal paths, tokens, or secrets.",
    `- Write ONLY skills/${candidate.slug}/SKILL.md; do not touch other files.`,
    "",
  ].join("\n");
}

// ── The command ──────────────────────────────────────────────────────────────

export async function runDistill(args: string[], opts: DistillCliOptions = {}): Promise<number> {
  const out = opts.out ?? ((line: string) => console.log(line));
  const err = opts.err ?? ((line: string) => console.error(line));
  const cwd = opts.cwd ?? process.cwd();
  const isTTY = opts.isTTY ?? Boolean(process.stdin.isTTY && process.stdout.isTTY);
  const ask = opts.ask ?? stdinAsk;

  // ── --publish <slug>: consent-gated preview of the exact outbound text ────
  if (args.includes("--publish")) {
    const slug = getFlag("publish", args);
    if (!slug) {
      err("usage: aethereum distill --publish <slug>");
      return 1;
    }
    if (!isTTY) {
      err("aethereum distill --publish needs an interactive terminal: publishing is consent-gated and the confirmation prompt cannot run without one.");
      return 1;
    }
    const skillPath = join(cwd, "skills", slug, "SKILL.md");
    if (!existsSync(skillPath)) {
      err(`No skills/${slug}/SKILL.md found.`);
      err(`Run 'aethereum distill' to write skills/${slug}/DRAFT.md, then have your own agent author the SKILL.md from it, e.g.:`);
      err(`  claude "$(cat skills/${slug}/DRAFT.md)"`);
      return 1;
    }
    let skillText: string;
    try {
      skillText = readFileSync(skillPath, "utf8");
    } catch {
      err(`Cannot read ${join("skills", slug, "SKILL.md")}.`);
      return 1;
    }

    out(`This is the EXACT text that would leave this machine (skills/${slug}/SKILL.md, ${skillText.length} chars):`);
    out("");
    out("──────────────────────── outbound payload ────────────────────────");
    out(skillText.replace(/\n$/, ""));
    out("───────────────────────── end of payload ─────────────────────────");
    out("");
    out("Where do you want it to go?");
    out("  [1] Append to the team ruleset (rides get_team_context for every agent in the room)");
    out("  [2] Submit to the skills marketplace (browser, signed in)");
    out("  [n] Cancel");
    const answer = (await ask("Choice [1/2/n]: ")).trim().toLowerCase();

    if (answer === "1") {
      // Checked, not invented: HostedWriteClient has no ruleset write and the
      // hosted bridge POST /api/sync/command has no set_ruleset action today.
      // The one existing ruleset write rail is the aethereum__set_ruleset MCP
      // tool your agent already has — so we print, we do not send.
      out("");
      out("The CLI has no hosted ruleset write yet; your agent does. Ask any agent in this room to run the");
      out("aethereum__set_ruleset MCP tool with the current ruleset text plus this appended:");
      out("");
      out(skillText.replace(/\n$/, ""));
      out("");
      out("Nothing was sent by this command.");
      return 0;
    }
    if (answer === "2") {
      const project = resolveProject(cwd, opts.home);
      const base = (project?.url ?? "https://www.aethereum.dev").replace(/\/$/, "");
      out("");
      out(`Marketplace submissions are browser-session-authed, so the CLI never POSTs them. Open:`);
      out(`  ${base}/skills/submit`);
      out("");
      out("and paste the payload above into the instructions field (name/description come from its frontmatter).");
      out("Nothing was sent by this command.");
      return 0;
    }
    out("Cancelled. Nothing was sent.");
    return 0;
  }

  // ── Scan (shared by --scan and the default drafting path) ────────────────
  const scan = await scanForCandidates({ home: opts.home, codexDir: opts.codexDir });
  // Persist the honest count so the opt-in usage publisher can carry it.
  persistDistillScan(scan.candidates.length, opts.home, opts.now);

  if (args.includes("--weekly")) {
    printWeekly(scan, out, opts.home);
    return 0;
  }

  if (args.includes("--scan")) {
    printReport(scan, out, opts.home);
    return 0;
  }

  // Default: scan, then write a DRAFT.md prompt per accepted candidate.
  printReport(scan, out, opts.home);
  if (scan.candidates.length === 0) return 0;
  out("");

  let written = 0;
  for (const c of scan.candidates) {
    if (isTTY && !args.includes("--yes")) {
      const answer = (await ask(`Write a draft skill prompt for "${c.slug}"? [Y/n] `)).trim().toLowerCase();
      if (answer === "n" || answer === "no") continue;
    }
    const dir = join(cwd, "skills", c.slug);
    const draftPath = join(dir, "DRAFT.md");
    try {
      mkdirSync(dir, { recursive: true });
      writeFileSync(draftPath, buildDraftPrompt(c), "utf8");
    } catch {
      err(`Could not write skills/${c.slug}/DRAFT.md, skipping.`);
      continue;
    }
    written++;
    out(`Wrote skills/${c.slug}/DRAFT.md. Have YOUR agent author the skill from it (we never call a model for you):`);
    out(`  claude "$(cat skills/${c.slug}/DRAFT.md)"`);
  }
  out("");
  out(
    written === 0
      ? "No drafts written."
      : `${written} draft${written === 1 ? "" : "s"} written. Once your agent has produced skills/<slug>/SKILL.md, preview publishing with 'aethereum distill --publish <slug>'.`,
  );
  return 0;
}
